<!-- src/components/DashboardView.vue -->
<script setup>
import { computed, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";

import { useDashboardStore } from "@/stores/useDashboardStore";
import { useTextStore } from "@/stores/useTextStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useUserStore } from "@/stores/useUserStore";
import { useActivityGeneralStore } from "@/stores/useActivityGeneralStore";
import { useNoteGeneralStore } from "@/stores/useNoteGeneralStore";

import dashboardService from "@/services/dashboardService";
import { toTitleCase } from "@/utils/stringUtils";
import { coverStyle, coverTombo } from "@/utils/textCover";

// ✅ ApexCharts (Vue 3)
import VueApexCharts from "vue3-apexcharts";
const apexchart = VueApexCharts;

import MainFollowModal from "@/components/Follow/MainFollowModal.vue";
import UserCardModal from "@/components/User/UserCardModal.vue";
import NoteCardModal from "@/components/Note/NoteCardModal.vue";

const dashboardStore = useDashboardStore();
const textStore = useTextStore();
const activityStore = useActivityStore();
const userStore = useUserStore();
const activityGeneralStore = useActivityGeneralStore();
const noteGeneralStore = useNoteGeneralStore();

const router = useRouter();

// Hint exibido ao passar o mouse sobre o avatar. Os nomes ao lado dos avatares
// mostram o username; o nome completo aparece só aqui.
function fullNameHint(fullName) {
  const name = (fullName ?? "").trim();
  return name ? `${name}` : undefined;
}

// Ranking: nomes com mais de 20 caracteres viram "...". Sem isso, um username
// grande empurra a contagem de notas para baixo repetidamente na lista toda.
function truncateName(name, max = 20) {
  const str = (name ?? "").trim();
  return str.length > max ? `${str.slice(0, max)}...` : str;
}

// ── Cards "Meus/Minhas": navegam para o form correspondente já com o
// filtro do usuário logado ativado (o form lê o flag da store no mount). ──
function goToMyTexts() {
  textStore.setMyTextsOnly(true);
  router.push({ name: "TextForm" });
}
function goToMyActivities() {
  activityGeneralStore.setFilter("onlyMine", true);
  router.push({ name: "ActivityGeneralForm" });
}
function goToMyNotes() {
  noteGeneralStore.setFilter("onlyMine", true);
  router.push({ name: "NoteGeneralForm" });
}

// 🔹 userId atual (para o NoteCardModal)
const currentUserId = computed(
  () => userStore.currentUser?.userId ?? userStore.user?.userId ?? null
);

// ✅ Compatível com a STORE atual (note o notesByMonth)
const {
  userStats,
  lastAnnotatedTexts,
  followedActivities,
  activeUsers,
  rankingByNotes,
  notesByMonth,
  isLoading,
  error,
} = storeToRefs(dashboardStore);

// ✅ range do gráfico compatível com controller: notes-chart?range=...
const selectedRange = ref("month");

// ======================================================
// ✅ FIX: concorrência de requests + re-render confiável
// ======================================================
const isChartLoading = ref(false);
const chartKey = ref(0);
let chartReqId = 0;

onMounted(async () => {
  await dashboardStore.fetchDashboard({
    lastTextsLimit: 20,
    followedLimit: 20,
    activeUsersLimit: 20,
    rankingLimit: 20,
  });

  await updateChartRange(selectedRange.value);
});

async function updateChartRange(range) {
  selectedRange.value = range;

  const reqId = ++chartReqId;
  isChartLoading.value = true;

  try {
    const data = await dashboardService.getNotesChart(range);
    if (reqId !== chartReqId) return; // ✅ ignora resposta velha

    const safe = Array.isArray(data) ? data : [];

    // normaliza pro formato único: { label, value, dateReference? }
    dashboardStore.notesByMonth = safe.map((p) => ({
      label:
        p.label ??
        p.monthLabel ??
        p.dayLabel ??
        p.yearLabel ??
        (p.month != null && p.year != null
          ? `${String(p.month).padStart(2, "0")}/${p.year}`
          : String(p.period ?? p.key ?? "")),
      value: p.value ?? p.totalNotes ?? p.notesCount ?? 0,
      dateReference: p.dateReference ?? null,
    }));

    chartKey.value++; // ✅ força re-mount do apexchart
  } catch (e) {
    if (reqId !== chartReqId) return; // ✅ só a última pode zerar
    console.error("❌ Erro ao carregar notes-chart:", e);
    dashboardStore.notesByMonth = [];
    chartKey.value++;
  } finally {
    if (reqId === chartReqId) isChartLoading.value = false;
  }
}

// ======================================================
// 🔹 Lógicas de Controle e Modais
// ======================================================
const showFollowModal = ref(false);
const followModalInitialView = ref("summary");

function openFollowModal(kind) {
  followModalInitialView.value = kind === "following" ? "personal" : "summary";
  showFollowModal.value = true;
}

const showUserCardModal = ref(false);
const selectedUserIdForCard = ref(null);

function openUserCard(userId) {
  if (!userId) return;
  selectedUserIdForCard.value = userId;
  showUserCardModal.value = true;
}

const showNoteCardModal = ref(false);
const noteIdToView = ref(null);

function openNoteCardFromAudit(noteId) {
  if (!noteId) return;
  noteIdToView.value = noteId;
  showNoteCardModal.value = true;
}

function handleNoteCardModalClose() {
  showNoteCardModal.value = false;
  noteIdToView.value = null;
}

// ======================================================
// 🔹 Textos anotados ordenados
// ======================================================
const sortedLastAnnotatedTexts = computed(() => {
  if (!lastAnnotatedTexts.value) return [];
  return [...lastAnnotatedTexts.value]
    .sort((a, b) => {
      const ta = a.lastNoteAt ? new Date(a.lastNoteAt).getTime() : 0;
      const tb = b.lastNoteAt ? new Date(b.lastNoteAt).getTime() : 0;
      return tb - ta;
    })
    .map((t) => ({ ...t, topicChips: buildTopicChips(t.topicNames, t.topicIds) }));
});

// ======================================================
// 🔹 Chips de assuntos (topics)
// ======================================================
// A cor sai de um hash do assunto, então o mesmo assunto aparece sempre com a
// mesma cor em toda a lista (e entre recargas do dashboard).
// As classes estão escritas por extenso porque o Tailwind varre o arquivo em
// busca de nomes literais — classes montadas por concatenação seriam purgadas.
const TOPIC_CHIP_COLORS = [
  "bg-indigo-50 text-indigo-700",
  "bg-emerald-50 text-emerald-700",
  "bg-amber-50 text-amber-700",
  "bg-sky-50 text-sky-700",
  "bg-rose-50 text-rose-700",
  "bg-violet-50 text-violet-700",
  "bg-teal-50 text-teal-700",
  "bg-orange-50 text-orange-700",
];

function topicColorIndex(topicId, topicName) {
  const id = Number(topicId);
  let seed = Number.isFinite(id) && id > 0 ? id : 0;

  // Sem id utilizável (payload antigo), o nome vira a semente.
  if (!seed) {
    const name = String(topicName ?? "");
    for (let i = 0; i < name.length; i++) {
      seed = (seed * 31 + name.charCodeAt(i)) % 100000;
    }
  }

  return seed % TOPIC_CHIP_COLORS.length;
}

// Monta os chips de um texto garantindo cores distintas na mesma linha: dois
// assuntos podem cair no mesmo índice do hash, então quem chega depois anda na
// paleta até achar uma cor livre. Só repete se o texto tiver mais assuntos do
// que cores disponíveis.
function buildTopicChips(topicNames, topicIds) {
  const names = Array.isArray(topicNames) ? topicNames : [];
  const ids = Array.isArray(topicIds) ? topicIds : [];
  const usedColors = new Set();

  return names.map((name, idx) => {
    let colorIdx = topicColorIndex(ids[idx], name);

    for (
      let step = 0;
      step < TOPIC_CHIP_COLORS.length && usedColors.has(colorIdx);
      step++
    ) {
      colorIdx = (colorIdx + 1) % TOPIC_CHIP_COLORS.length;
    }
    usedColors.add(colorIdx);

    return {
      id: ids[idx] ?? null,
      name,
      colorClass: TOPIC_CHIP_COLORS[colorIdx],
    };
  });
}

// ======================================================
// 🔹 Navegação
// ======================================================
// "Ver atividades" LISTA as atividades do texto — vai para o ActivityForm, não
// para a tela de anotação.
//
// O f8a6c2c passou esta função a abrir a série resolvida direto na RightArea.
// Faz sentido para quem clica no título (o destino é uma anotação), mas não
// aqui: o rótulo promete a lista, e pular direto para uma série tira do usuário
// justamente a escolha de qual série abrir. Quem leva à anotação nesta linha é o
// título, ao lado.
async function goToActivities(textId) {
  if (!textId) return;
  try {
    await textStore.getTextById(textId);
    const textTitle = textStore.selectedText?.title;
    router.push({ name: "ActivityForm", params: { textId, textTitle } });
  } catch (error) {
    console.error("❌ Erro ao navegar para atividades:", error);
  }
}

// Clique no título, na tabela "Textos mais recentes com anotações da
// comunidade": abre a ATIVIDADE em que a última nota foi postada — a mesma nota
// cujo autor e horário a linha mostra à direita. Antes ia para o TextReader, que
// é leitura sem notas: o dashboard anunciava uma anotação e levava para onde ela
// não aparece.
//
// O fallback não é zelo excessivo: `lastNoteActivityId` é campo novo do
// /Dashboard/last-annotated-texts, e entre o deploy do front e o do back ele
// chega ausente. Sem ele, o título ficaria sem clique nessa janela; com ele,
// cai na LISTA de atividades do texto — de onde a série da última nota está a
// um clique, em vez de a lugar nenhum.
function openLastNoteActivity(t) {
  if (!t?.id) return;
  if (t.lastNoteActivityId) {
    return goToRightAreaFromDashboard(t.id, t.lastNoteActivityId);
  }
  return goToActivities(t.id);
}

async function goToRightAreaFromDashboard(textId, activityId) {
  if (!textId || !activityId) return;

  try {
    const tId = parseInt(textId);
    const aId = parseInt(activityId);
    const finalTextId = !isNaN(tId) && tId > 0 ? tId : textId;
    const finalActivityId = !isNaN(aId) && aId > 0 ? aId : activityId;

    // A série vem explícita do card do dashboard — passa como preferida; o
    // store cai no resolve se ela não existir mais / perdeu acesso.
    await activityStore.openTextForAnnotation(finalTextId, {
      preferActivityId: finalActivityId,
    });

    router.push({ name: "RightArea" });
  } catch (error) {
    console.error("❌ Erro ao navegar para RightArea:", error);
  }
}

// ======================================================
// 🔹 Auditoria (mantido)
// ======================================================
function getAuditContext(entry) {
  if (!entry)
    return {
      resourceTypeRaw: "",
      resourceId: null,
      resourceName: "",
      textId: null,
      textName: "",
      activityId: null,
      activityName: "",
    };

  const rawType =
    (entry.resourceType || entry.targetType || "").toString().trim() || "";
  let resourceTypeRaw = rawType.toLowerCase();

  let resourceId = entry.resourceId ?? entry.ResourceId ?? null;

  let textId =
    entry.relatedTextId ??
    entry.RelatedTextId ??
    entry.textId ??
    entry.TextId ??
    null;

  if (!textId && (resourceTypeRaw === "text" || rawType === "Text")) {
    textId =
      entry.textId ??
      entry.TextId ??
      entry.id ??
      entry.Id ??
      resourceId ??
      null;
  }

  let resourceName = (entry.resourceName || entry.ResourceName || "")
    .toString()
    .trim();

  let textName = (
    entry.relatedTextName ||
    entry.RelatedTextName ||
    entry.textTitle ||
    entry.TextTitle ||
    entry.textName ||
    entry.TextName ||
    entry.title ||
    entry.Title ||
    ""
  )
    .toString()
    .trim();

  let activityId =
    entry.relatedActivityId ??
    entry.RelatedActivityId ??
    entry.activityId ??
    entry.ActivityId ??
    null;

  let activityName = (
    entry.relatedActivityName ||
    entry.RelatedActivityName ||
    entry.activityTitle ||
    entry.ActivityTitle ||
    ""
  )
    .toString()
    .trim();

  if (!resourceTypeRaw && (textId || textName)) resourceTypeRaw = "text";
  if (!textId && resourceTypeRaw === "text" && resourceId != null)
    textId = resourceId;

  if (resourceTypeRaw === "text") {
    if (!textName && resourceName) textName = resourceName;
    if (!textName && textId != null) textName = `Texto #${textId}`;
  }

  if (!activityId && resourceTypeRaw === "activity" && resourceId != null)
    activityId = resourceId;
  if (!activityName && resourceTypeRaw === "activity" && resourceName)
    activityName = resourceName;

  return {
    resourceTypeRaw,
    resourceId,
    resourceName,
    textId,
    textName,
    activityId,
    activityName,
  };
}

const actionVerbMap = {
  login: "Fez login",
  login_google: "Fez login com o Google",
  delete_batch: "Excluiu",
  delete: "Excluiu",
  view: "Visualizou",
  update: "Editou",
  update_batch: "Atualizou",
  register: "Registrou",
  activate: "Ativou",
  create: "Criou",
  create_internal: "Criou",
};

function buildAuditDisplayParts(entry) {
  const parts = [];
  if (!entry) return parts;

  const explicitAction = (entry.action || "").toString().trim();
  const lastActionDesc = (entry.lastAction || entry.actionDescription || "")
    .toString()
    .trim();

  let verb = explicitAction
    ? actionVerbMap[explicitAction.toLowerCase()] || explicitAction
    : lastActionDesc || "Realizou";

  verb = verb.charAt(0).toUpperCase() + verb.slice(1);

  const actionLower = explicitAction.toLowerCase();
  const isDeleteAction = actionLower.includes("delete");

  const ctx = getAuditContext(entry);
  let {
    resourceTypeRaw,
    resourceId,
    resourceName,
    textId,
    textName,
    activityId,
    activityName,
  } = ctx;

  const isNumeric = (val) =>
    val !== "" && /^[0-9]+$/.test(val.toString().trim());
  if (isNumeric(resourceName)) resourceName = "";
  if (isNumeric(textName)) textName = "";
  if (isNumeric(activityName)) activityName = "";

  if (actionLower === "login" || actionLower === "login_google") {
    parts.push({ type: "plain", text: verb });
    return parts;
  }

  if (!resourceTypeRaw) {
    parts.push({ type: "plain", text: verb });
    return parts;
  }

  let typeLabel =
    resourceTypeRaw === "note"
      ? "nota"
      : resourceTypeRaw === "text"
      ? "texto"
      : resourceTypeRaw === "activity"
      ? "atividade"
      : resourceTypeRaw === "tag"
      ? "tag"
      : resourceTypeRaw;

  let article =
    ["nota", "atividade", "tag"].includes(typeLabel) ||
    typeLabel.endsWith("ção") ||
    typeLabel.endsWith("são")
      ? "a"
      : "o";

  if (resourceTypeRaw === "text") {
    parts.push({ type: "plain", text: `${verb} ${article} ${typeLabel} ` });
    if (textName)
      parts.push({
        type: textId && !isDeleteAction ? "textLink" : "strong",
        text: textName,
        textId,
      });
    return parts;
  }

  if (resourceTypeRaw === "tag") {
    parts.push({ type: "plain", text: `${verb} ${article} ${typeLabel} ` });
    if (resourceName) parts.push({ type: "strong", text: resourceName });
    if (textName) {
      parts.push({ type: "plain", text: " no texto " });
      parts.push({
        type: textId && !isDeleteAction ? "textLink" : "plain",
        text: textName,
        textId,
      });
    }
    return parts;
  }

  if (resourceTypeRaw === "activity") {
    parts.push({ type: "plain", text: `${verb} ${article} ${typeLabel} ` });
    const activityLabel = activityName || resourceName || "";
    if (activityLabel)
      parts.push({
        type:
          activityId && textId && !isDeleteAction ? "activityLink" : "strong",
        text: activityLabel,
        textId,
        activityId,
      });
    if (textName) {
      parts.push({ type: "plain", text: " no texto " });
      parts.push({
        type: textId ? "textLink" : "plain",
        text: textName,
        textId,
      });
    }
    return parts;
  }

  if (resourceTypeRaw === "note") {
    parts.push({ type: "plain", text: `${verb} ${article} ${typeLabel} ` });
    if (resourceName)
      parts.push({
        type: resourceId && !isDeleteAction ? "noteLink" : "strong",
        text: resourceName,
        noteId: resourceId,
      });
    if (activityName) {
      parts.push({ type: "plain", text: " na atividade " });
      parts.push({
        type: activityId && textId ? "activityLink" : "strong",
        text: activityName,
        textId,
        activityId,
      });
    }
    if (textName) {
      parts.push({ type: "plain", text: " no texto " });
      parts.push({
        type: textId ? "textLink" : "plain",
        text: textName,
        textId,
      });
    }
    return parts;
  }

  parts.push({ type: "plain", text: verb });
  if (typeLabel)
    parts.push({ type: "plain", text: ` ${article} ${typeLabel}` });
  if (resourceName) parts.push({ type: "strong", text: ` ${resourceName}` });
  if (textName) {
    parts.push({ type: "plain", text: " no texto " });
    parts.push({ type: textId ? "textLink" : "plain", text: textName, textId });
  }
  return parts;
}

// ======================================================
// 🔹 ApexCharts (Spline Area) usando notesByMonth
// ======================================================
const notesBars = computed(() =>
  Array.isArray(notesByMonth.value) ? notesByMonth.value : []
);

const chartCategories = computed(() => notesBars.value.map((p) => p.label));
const chartValues = computed(() => notesBars.value.map((p) => p.value || 0));

const apexSeries = computed(() => [
  {
    name: "Notas",
    data: chartValues.value,
  },
]);

const chartOptions = computed(() => ({
  chart: {
    type: "area",
    toolbar: { show: false },
    zoom: { enabled: false },
    animations: { enabled: true },
    fontFamily: "inherit",
  },
  stroke: {
    curve: "smooth",
    width: 3,
  },
  dataLabels: { enabled: false },
  markers: {
    size: 0,
    hover: { size: 5 },
  },
  grid: {
    strokeDashArray: 3,
    padding: { left: 8, right: 12, top: 0, bottom: 0 },
  },
  xaxis: {
    categories: chartCategories.value,
    labels: {
      rotate: 0,
      style: { fontSize: "11px" },
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
    tooltip: { enabled: false },
  },
  yaxis: {
    labels: { style: { fontSize: "11px" } },
    min: 0,
    forceNiceScale: true,
  },
  tooltip: {
    y: {
      formatter: (val) => `${val} nota${val === 1 ? "" : "s"}`,
    },
  },
  fill: {
    type: "gradient",
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.35,
      opacityTo: 0.05,
      stops: [0, 90, 100],
    },
  },
  legend: { show: false },
}));

// Seguidores/seguindo
const followingCount = computed(() => userStats.value?.following ?? 0);
const followerCount = computed(() => userStats.value?.followers ?? 0);

// Tempo relativo
function formatRelativeTime(value) {
  if (!value) return "";
  if (
    typeof value === "string" &&
    (value.startsWith("há ") || value === "agora mesmo")
  )
    return value;

  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value);

  const now = new Date();
  const diffMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

  let minutes = diffMinutes;
  if (minutes < 0 && minutes >= -2) minutes = 0;

  if (minutes < 0) {
    const fMin = Math.abs(minutes);
    if (fMin < 60) return `em ${fMin} min`;
    const fHrs = Math.floor(fMin / 60);
    if (fHrs < 24) return `em ${fHrs} h`;
    const fDays = Math.floor(fHrs / 24);
    return `em ${fDays} ${fDays === 1 ? "dia" : "dias"}`;
  }

  if (minutes < 1) return "agora mesmo";
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;

  const days = Math.floor(hours / 24);
  return `há ${days} ${days === 1 ? "dia" : "dias"}`;
}

// Texto do gráfico
const chartUnitTitle = computed(() => {
  if (selectedRange.value === "day") return "dia";
  if (selectedRange.value === "year") return "ano";
  return "mês";
});
</script>

<template>
  <div class="bg-slate-0 p-2 rounded-2xl">
    <!-- ✅ NOVO: spinner sobreposto ao conteúdo, centralizado horizontalmente,
         posicionado a ~20% do topo da janela, visível enquanto isLoading === true -->
    <Teleport to="body">
      <div v-if="isLoading" class="dashboard-loading-overlay">
        <svg class="dashboard-spinner" viewBox="0 0 24 24" fill="none">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
      </div>
    </Teleport>

    <div class="space-y-4">
      <div class="pt-0 pb-0 flex items-center justify-between">
        <h1 class="text-xl font-bold">Painel geral</h1>
      </div>

      <p class="text-xs text-slate-500">
        Esses dados são carregados dinamicamente a partir da sua atividade no
        Litterae.
      </p>

      <div
        v-if="error"
        class="rounded-xl bg-red-50 border border-red-200 px-4 py-2 text-xs text-red-700"
      >
        {{ error }}
      </div>

      <!-- PRIMEIRO BLOCO -->
      <section class="grid gap-6 lg:grid-cols-3">
        <div class="lg:col-span-2 space-y-4">
          <section class="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            <div
              role="button"
              tabindex="0"
              title="Ver meus textos"
              class="rounded-2xl p-4 shadow border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white flex flex-col h-full cursor-pointer dash-card hover:shadow-md hover:border-indigo-300"
              @click="goToMyTexts"
              @keydown.enter="goToMyTexts"
            >
              <h2 class="text-sm font-semibold text-indigo-700">Meus Textos</h2>
              <p class="text-3xl font-bold text-indigo-900 mt-auto">
                {{ userStats.texts ?? 0 }}
              </p>
            </div>

            <div
              role="button"
              tabindex="0"
              title="Ver minhas atividades"
              class="rounded-2xl p-4 shadow border border-sky-100 bg-gradient-to-br from-sky-50 to-white flex flex-col h-full cursor-pointer dash-card hover:shadow-md hover:border-sky-300"
              @click="goToMyActivities"
              @keydown.enter="goToMyActivities"
            >
              <h2 class="text-sm font-semibold text-sky-700">
                Minhas Atividades
              </h2>
              <p class="text-3xl font-bold text-sky-900 mt-auto">
                {{ userStats.activities ?? 0 }}
              </p>
            </div>

            <div
              role="button"
              tabindex="0"
              title="Ver minhas notas"
              class="rounded-2xl p-4 shadow border border-amber-100 bg-gradient-to-br from-amber-50 to-white flex flex-col h-full cursor-pointer dash-card hover:shadow-md hover:border-amber-300"
              @click="goToMyNotes"
              @keydown.enter="goToMyNotes"
            >
              <h2 class="text-sm font-semibold text-amber-700">Minhas Notas</h2>
              <p class="text-3xl font-bold text-amber-900 mt-auto">
                {{ userStats.notes ?? 0 }}
              </p>
            </div>

            <div
              role="button"
              tabindex="0"
              title="Ver quem eu sigo"
              class="rounded-2xl p-4 shadow border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white flex flex-col h-full cursor-pointer dash-card hover:shadow-md hover:border-emerald-300"
              @click="openFollowModal('following')"
              @keydown.enter="openFollowModal('following')"
            >
              <h2 class="text-sm font-semibold text-emerald-700">Seguindo</h2>
              <p class="text-3xl font-bold text-emerald-900 mt-auto">
                {{ followingCount }}
              </p>
            </div>

            <div
              role="button"
              tabindex="0"
              title="Ver meus seguidores"
              class="rounded-2xl p-4 shadow border border-pink-100 bg-gradient-to-br from-pink-50 to-white flex flex-col h-full cursor-pointer dash-card hover:shadow-md hover:border-pink-300"
              @click="openFollowModal('followers')"
              @keydown.enter="openFollowModal('followers')"
            >
              <h2 class="text-sm font-semibold text-pink-700">Seguidores</h2>
              <p class="text-3xl font-bold text-pink-900 mt-auto">
                {{ followerCount }}
              </p>
            </div>
          </section>

          <!-- Últimos textos anotados -->
          <div class="rounded-2xl bg-surface p-5 shadow">
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 min-w-0">
                <div class="min-w-0">
                  <h2 class="text-base font-semibold text-slate-800 truncate">
                    Textos mais recentes com anotações da comunidade
                  </h2>
                </div>
              </div>
            </div>

            <p
              v-if="
                !isLoading &&
                (!lastAnnotatedTexts || !lastAnnotatedTexts.length)
              "
              class="text-[11px] text-slate-400 mt-3"
            >
              Você ainda não anotou nenhum texto.
            </p>

            <div
              v-if="sortedLastAnnotatedTexts && sortedLastAnnotatedTexts.length"
              class="mt-3"
            >
              <div
                class="grid grid-cols-[minmax(0,2fr)_140px_170px] items-center gap-4 bg-slate-50 rounded-lg py-2 px-3 text-[11px] font-semibold text-slate-500"
              >
                <span class="text-left">Texto</span>
                <span class="text-center">Ação</span>
                <span class="text-center">Último que anotou</span>
              </div>

              <div class="max-h-96 overflow-y-auto pr-1">
                <ul class="divide-y divide-slate-100">
                  <li
                    v-for="t in sortedLastAnnotatedTexts"
                    :key="t.id"
                    class="py-3 px-3 grid grid-cols-[minmax(0,2fr)_140px_170px] items-center gap-2"
                  >
                    <div class="flex items-start gap-3 min-w-0">
                      <!-- Capa do texto, à esquerda: é por ela que o texto é
                           reconhecido na estante e no leitor, e a lista aqui
                           passa a falar a mesma língua. Já chega recortada em
                           3:4 por utils/coverImage.js.

                           Leva ao mesmo lugar que o título ao lado — a
                           atividade da última nota. Ela é o maior alvo da
                           linha; deixá-la inerte ao lado de um título clicável
                           fazia o clique natural não dar em nada. -->
                      <button
                        type="button"
                        class="dash-cover-link"
                        :title="`Abrir a atividade da última nota de ${t.title}`"
                        :aria-label="`Abrir a atividade da última nota de ${t.title}`"
                        @click="openLastNoteActivity(t)"
                      >
                        <img
                          v-if="t.cover"
                          :src="t.cover"
                          :alt="`Capa de ${t.title}`"
                          loading="lazy"
                          decoding="async"
                          class="dash-cover object-cover bg-slate-200"
                        />

                        <!-- Sem imagem enviada: a capa desenhada a partir do
                             próprio texto, a MESMA que a estante desenha (mesmo
                             hash do título, mesma paleta). Não há estado
                             vazio. -->
                        <span
                          v-else
                          class="dash-cover dash-cover--drawn"
                          :style="coverStyle({ title: t.title })"
                          aria-hidden="true"
                        >
                          <span class="dash-cover-title">{{
                            toTitleCase(t.title)
                          }}</span>
                          <span class="dash-cover-tombo">{{
                            coverTombo({ textId: t.id })
                          }}</span>
                        </span>
                      </button>

                      <div class="flex flex-col min-w-0">
                        <!-- Título do texto abre a atividade da última nota —
                             a nota cujo autor e horário aparecem nesta mesma
                             linha. -->
                        <button
                          type="button"
                          class="text-sm font-medium text-slate-800 text-left hover:text-indigo-700 hover:underline truncate"
                          :title="`Abrir a atividade da última nota de ${t.title}`"
                          @click="openLastNoteActivity(t)"
                        >
                          {{ t.title }}
                        </button>

                        <p
                          v-if="t.subtitle"
                          class="text-xs text-slate-500 truncate"
                        >
                          {{ t.subtitle }}
                        </p>

                        <!-- Contagem de notas + assuntos do texto na mesma
                             linha; os chips quebram para a linha de baixo
                             quando o texto tem muitos assuntos. -->
                        <div class="flex items-center flex-wrap gap-1.5 mt-1">
                          <span
                            class="inline-flex items-center gap-1 text-[10px] font-medium text-slate-600 bg-slate-100 rounded-full px-2 py-0.5 w-fit"
                          >
                            <i class="fas fa-layer-group text-[9px]"></i>
                            {{ t.noteCount }}
                            {{ t.noteCount === 1 ? "nota" : "notas" }}
                          </span>

                          <span
                            v-for="(chip, idx) in t.topicChips"
                            :key="chip.id ?? `${chip.name}-${idx}`"
                            :title="`Assunto: ${chip.name}`"
                            class="inline-flex items-center max-w-[160px] text-[10px] font-medium rounded-full px-2 py-0.5 truncate"
                            :class="chip.colorClass"
                          >
                            {{ chip.name }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="flex justify-center">
                      <button
                        type="button"
                        class="dash-outline-btn whitespace-nowrap"
                        @click="goToActivities(t.id)"
                      >
                        Ver atividades
                      </button>
                    </div>

                    <div class="flex items-center gap-2 min-w-0">
                      <img
                        v-if="t.avatar"
                        :src="t.avatar"
                        :title="t.userName"
                        class="h-8 w-8 rounded-full object-cover shadow-sm border border-slate-100 cursor-pointer flex-shrink-0"
                        @click="openUserCard(t.userId)"
                      />
                      <div
                        v-else
                        :title="t.userName"
                        class="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 cursor-pointer flex-shrink-0"
                        @click="openUserCard(t.userId)"
                      >
                        <i class="fas fa-user text-xs"></i>
                      </div>

                      <div class="flex flex-col min-w-0">
                        <button
                          type="button"
                          class="text-xs font-medium text-slate-800 text-left hover:text-indigo-700 truncate"
                          @click="openUserCard(t.userId)"
                        >
                          {{ t.userName }}
                        </button>
                        <span
                          class="inline-flex items-center gap-1 text-[11px] text-slate-400 truncate"
                        >
                          <i class="far fa-clock text-[10px]"></i>
                          {{ formatRelativeTime(t.lastNoteAt) }}
                        </span>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Atividades de quem você segue -->
        <div
          class="rounded-2xl bg-surface p-5 shadow flex flex-col lg:min-h-0 lg:overflow-hidden"
        >
          <h2 class="text-sm font-semibold text-slate-700 mb-3">
            Atividades de quem você segue
          </h2>

          <div class="flex-1 min-h-0 lg:relative">
            <div
              v-if="followedActivities && followedActivities.length"
              class="overflow-y-auto pr-1 lg:absolute lg:inset-0"
            >
              <ul class="space-y-3">
                <li
                  v-for="a in followedActivities"
                  :key="a.id"
                  class="bg-slate-50 rounded-xl px-3 py-2 flex items-start gap-3"
                >
                  <div class="flex-shrink-0">
                    <img
                      v-if="a.avatar"
                      :src="a.avatar"
                      :title="fullNameHint(a.fullName)"
                      class="h-8 w-8 rounded-full object-cover mt-0.5 cursor-pointer"
                      @click="openUserCard(a.userId)"
                    />
                    <div
                      v-else
                      :title="fullNameHint(a.fullName)"
                      class="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 mt-0.5 cursor-pointer"
                      @click="openUserCard(a.userId)"
                    >
                      <i class="fas fa-user text-xs"></i>
                    </div>
                  </div>

                  <div class="flex-1">
                    <p class="text-xs font-medium text-slate-800">
                      {{ a.userName }}
                    </p>

                    <p
                      class="text-xs text-slate-500"
                      style="
                        text-align: justify;
                        hyphens: auto;
                        word-break: break-word;
                        overflow-wrap: break-word;
                      "
                    >
                      <template
                        v-for="(seg, idx) in buildAuditDisplayParts(a)"
                        :key="idx"
                      >
                        <span v-if="seg.type === 'plain'">{{ seg.text }}</span>

                        <strong v-else-if="seg.type === 'strong'">{{
                          seg.text
                        }}</strong>

                        <button
                          v-else-if="seg.type === 'noteLink'"
                          type="button"
                          class="text-indigo-600 hover:text-indigo-800 hover:underline"
                          @click="openNoteCardFromAudit(seg.noteId)"
                        >
                          {{ seg.text }}
                        </button>

                        <button
                          v-else-if="seg.type === 'activityLink'"
                          type="button"
                          class="text-indigo-600 hover:text-indigo-800 hover:underline"
                          @click="
                            goToRightAreaFromDashboard(
                              seg.textId,
                              seg.activityId
                            )
                          "
                        >
                          {{ seg.text }}
                        </button>

                        <button
                          v-else-if="seg.type === 'textLink'"
                          type="button"
                          class="text-indigo-600 hover:text-indigo-800 hover:underline"
                          @click="goToActivities(seg.textId)"
                        >
                          {{ seg.text }}
                        </button>
                      </template>
                    </p>

                    <p
                      class="inline-flex items-center gap-1 text-[11px] text-slate-400"
                    >
                      <i class="far fa-clock text-[10px]"></i>
                      {{ formatRelativeTime(a.when) }}
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div
              v-else-if="!isLoading"
              class="flex-1 lg:absolute lg:inset-0 flex flex-col items-center justify-center text-center py-6 text-slate-400"
            >
              <div
                class="h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3"
              >
                <i class="fas fa-tasks fa-lg text-indigo-400"></i>
              </div>
              <p class="text-xs font-semibold text-slate-600 mb-1">
                Ainda não há atividades por aqui
              </p>
              <p class="text-[11px] text-slate-400 max-w-xs">
                Comece a seguir outros leitores para ver as atividades deles
                aparecendo neste painel.
                <strong>Basta clicar sobre as fotos deles.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- SEGUNDO BLOCO -->
      <section class="grid gap-6 lg:grid-cols-3">
        <!-- Gráfico -->
        <div class="rounded-2xl bg-surface p-5 shadow lg:col-span-2">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="text-base font-semibold text-slate-800">
                Minhas notas por {{ chartUnitTitle }}
              </h2>
              <p class="text-xs text-slate-500 mb-2">
                Total de notas criadas por
                {{
                  selectedRange === "day"
                    ? "dia"
                    : selectedRange === "month"
                    ? "mês"
                    : "ano"
                }}.
              </p>
            </div>

            <!-- ✅ Botões de range (com disable durante loading) -->
            <div class="flex bg-slate-100 p-1 rounded-lg w-fit">
              <button
                v-for="range in ['day', 'month', 'year']"
                :key="range"
                @click="updateChartRange(range)"
                :disabled="isChartLoading"
                class="px-3 py-1 text-xs font-medium rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                :class="
 selectedRange === range
                    ? 'bg-surface shadow text-indigo-600'
                    : 'text-slate-500 hover:text-slate-700'
                "
              >
                {{
                  range === "day" ? "Dia" : range === "month" ? "Mês" : "Ano"
                }}
              </button>
            </div>
          </div>

          <div
            v-if="isChartLoading"
            class="flex items-center justify-center text-center py-10 text-slate-400"
          >
            <span class="text-xs">Carregando gráfico...</span>
          </div>

          <div
            v-else-if="!notesBars || !notesBars.length"
            class="flex flex-col items-center justify-center text-center py-8 text-slate-400"
          >
            <div
              class="h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3"
            >
              <i class="fas fa-chart-line fa-lg text-indigo-400"></i>
            </div>
            <p class="text-xs font-semibold text-slate-600 mb-1">
              Ainda não há notas por aqui
            </p>
            <p class="text-[11px] text-slate-400 max-w-xs">
              Crie notas em seus textos para ver este gráfico ganhar
              forma.
            </p>
          </div>

          <!-- ✅ ApexCharts (Spline Area) -->
          <div v-else class="mt-2">
            <component
              :is="apexchart"
              :key="chartKey"
              type="area"
              height="280"
              :options="chartOptions"
              :series="apexSeries"
            />
          </div>
        </div>

        <!-- Coluna direita -->
        <div class="space-y-4">
          <!-- Últimos usuários ativos -->
          <div class="rounded-2xl bg-surface p-5 shadow">
            <div class="flex items-center justify-between mb-3">
              <h2 class="text-sm font-semibold text-slate-700">
                Últimos usuários ativos
              </h2>

              <span
                v-if="!isLoading && (!activeUsers || !activeUsers.length)"
                class="text-[11px] text-slate-400"
              >
                Nenhum usuário ativo recentemente.
              </span>
            </div>

            <div
              v-if="activeUsers && activeUsers.length"
              class="max-h-96 overflow-y-auto pr-1"
            >
              <ul class="space-y-2">
                <li
                  v-for="u in activeUsers"
                  :key="u.id"
                  class="bg-slate-50 rounded-xl px-3 py-2 flex items-start gap-3"
                >
                  <div class="flex-shrink-0">
                    <img
                      v-if="u.avatar"
                      :src="u.avatar"
                      :title="fullNameHint(u.fullName)"
                      class="h-8 w-8 rounded-full object-cover mt-0.5 cursor-pointer"
                      @click="openUserCard(u.id)"
                    />
                    <div
                      v-else
                      :title="fullNameHint(u.fullName)"
                      class="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 mt-0.5 cursor-pointer"
                      @click="openUserCard(u.id)"
                    >
                      <i class="fas fa-user text-xs"></i>
                    </div>
                  </div>

                  <div>
                    <p class="font-medium text-sm text-slate-800">
                      {{ u.name }}
                    </p>

                    <p
                      class="text-xs text-slate-500"
                      style="
                        text-align: justify;
                        hyphens: auto;
                        word-break: break-word;
                        overflow-wrap: break-word;
                      "
                    >
                      <template
                        v-for="(seg, idx) in buildAuditDisplayParts(u)"
                        :key="idx"
                      >
                        <span v-if="seg.type === 'plain'">{{ seg.text }}</span>
                        <strong v-else-if="seg.type === 'strong'">{{
                          seg.text
                        }}</strong>

                        <button
                          v-else-if="seg.type === 'noteLink'"
                          type="button"
                          class="text-indigo-600 hover:text-indigo-800 hover:underline"
                          @click="openNoteCardFromAudit(seg.noteId)"
                        >
                          {{ seg.text }}
                        </button>

                        <button
                          v-else-if="seg.type === 'activityLink'"
                          type="button"
                          class="text-indigo-600 hover:text-indigo-800 hover:underline"
                          @click="
                            goToRightAreaFromDashboard(
                              seg.textId,
                              seg.activityId
                            )
                          "
                        >
                          {{ seg.text }}
                        </button>

                        <button
                          v-else-if="seg.type === 'textLink'"
                          type="button"
                          class="text-indigo-600 hover:text-indigo-800 hover:underline"
                          @click="goToActivities(seg.textId)"
                        >
                          {{ seg.text }}
                        </button>
                      </template>
                    </p>

                    <p
                      class="inline-flex items-center gap-1 text-[11px] text-slate-400"
                    >
                      <i class="far fa-clock text-[10px]"></i>
                      {{ formatRelativeTime(u.when) }}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Ranking -->
          <div class="rounded-2xl bg-surface p-5 shadow">
            <h2 class="text-sm font-semibold text-slate-700 mb-3">
              Ranking - quem mais anotou
            </h2>

            <div
              v-if="rankingByNotes && rankingByNotes.length"
              class="max-h-96 overflow-y-auto pr-1"
            >
              <ol class="space-y-2">
                <li
                  v-for="u in rankingByNotes"
                  :key="u.position"
                  class="flex flex-wrap items-center gap-x-3 gap-y-1 bg-slate-50 rounded-xl px-3 py-2"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <span
                      class="flex items-center justify-center font-bold"
                      :class="[
 u.position === 1
                          ? 'text-xl text-yellow-600'
                          : u.position === 2
                          ? 'text-lg text-indigo-600'
                          : u.position === 3
                          ? 'text-base text-amber-600'
                          : 'text-sm text-slate-600',
                      ]"
                    >
                      {{ u.position }}
                    </span>

                    <div class="flex-shrink-0">
                      <img
                        v-if="u.avatar"
                        :src="u.avatar"
                        :title="fullNameHint(u.fullName)"
                        class="h-8 w-8 rounded-full object-cover cursor-pointer"
                        @click="openUserCard(u.id)"
                      />
                      <div
                        v-else
                        :title="fullNameHint(u.fullName)"
                        class="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 cursor-pointer"
                        @click="openUserCard(u.id)"
                      >
                        <i class="fas fa-user text-xs"></i>
                      </div>
                    </div>

                    <span
                      class="font-medium"
                      :class="
 u.isCurrentUser
                          ? 'text-indigo-700'
                          : 'text-slate-700 text-sm font-semibold'
                      "
                      :title="u.name"
                    >
                      {{ truncateName(u.name) }}
                    </span>
                  </div>

                  <span class="text-xs text-slate-500 flex justify-end w-16 ml-auto">
                    <span class="font-semibold">{{ u.notesCount }}</span
                    >&nbsp;notas
                  </span>
                </li>
              </ol>
            </div>

            <p v-else-if="!isLoading" class="text-[11px] text-slate-400">
              Ainda não há ranking disponível.
            </p>
          </div>
        </div>
      </section>
    </div>

    <MainFollowModal
      v-if="showFollowModal"
      :initialView="followModalInitialView"
      @close="showFollowModal = false"
    />

    <UserCardModal
      v-if="showUserCardModal && selectedUserIdForCard"
      :show="showUserCardModal"
      :userId="selectedUserIdForCard"
      @close="showUserCardModal = false"
    />

    <NoteCardModal
      v-if="showNoteCardModal && noteIdToView"
      :show="showNoteCardModal"
      :noteId="noteIdToView"
      :userId="currentUserId"
      @close="handleNoteCardModalClose"
    />
  </div>
</template>

<style scoped>
/* ── Capa do texto na lista "Textos mais recentes" ──────────────────────────
   Miniatura de lombada: pequena porque a linha é uma linha, e não um card —
   ela identifica o texto de relance, e quem lê o título já o tem ao lado.
   A PROPORÇÃO é a que não muda: 3:4, a mesma do recorte feito em
   utils/coverImage.js e a mesma da estante e do leitor. Largura e altura saem
   de uma variável só para não poderem divergir.

   A capa é um BOTÃO: leva ao mesmo lugar que o título ao lado — a atividade da
   última nota. O botão não desenha nada, só embrulha a imagem e responde ao
   clique, e por isso o tamanho e o recorte continuam sendo da capa.

   O `flex-shrink: 0` mora no botão, e não na capa: agora é ele o item da
   linha, e sem isso o título e as chips o espremem e a capa aparece
   deformada. */
.dash-cover-link {
  flex-shrink: 0;
  display: block;
  padding: 0;
  border: none;
  background: none;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.12s ease;
}

.dash-cover-link:hover {
  transform: translateY(-1px);
}

.dash-cover-link:hover .dash-cover {
  box-shadow: 0 3px 8px rgba(15, 23, 42, 0.28);
}

.dash-cover-link:active {
  transform: translateY(0);
}

.dash-cover {
  --dash-cover-w: 40px;

  display: block;
  width: var(--dash-cover-w);
  height: calc(var(--dash-cover-w) * 4 / 3);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
}

/* Capa desenhada: o gradiente vem de utils/textCover.js, e o que se lê é o
   próprio texto. Nesta escala cabem o título e o tombo — o autor, que a capa
   da estante mostra, não caberia legível e sairia. */
.dash-cover--drawn {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 3px;
  color: #fff;
  text-align: center;
}

.dash-cover-title {
  font-size: 5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1.3;
  text-transform: uppercase;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

/* Tombo encostado na base, como etiqueta de acervo. */
.dash-cover-tombo {
  margin-top: auto;
  font-size: 5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  opacity: 0.75;
}
/* Cards clicáveis: efeito de "afundar" ao clicar IDÊNTICO ao botão de suporte
   da topbar (.support-help-btn em DashboardLayout.vue) — transform 0.05s ease
   + translateY(1px) no :active. A transição de hover (sombra/borda) fica a
   0.2s, como o background do botão de suporte. */
.dash-card {
  transition: box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out,
    transform 0.05s ease;
}
.dash-card:active {
  transform: translateY(1px);
}

/* Botão outline dos painéis (Ver todos os textos / Ver atividades),
   conforme o mock: fundo branco, borda clara, texto índigo e chevron. */
.dash-outline-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.85rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #4f46e5;
  background-color: var(--surface-3);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
.dash-outline-btn:hover {
  background-color: var(--neutral-200);
}
.dash-outline-btn:active {
  transform: translateY(1px);
}

/* ✅ NOVO: overlay do spinner de carregamento do dashboard.
   Sobreposto ao conteúdo via position:fixed, centralizado horizontalmente,
   posicionado a 20% do topo da janela. Sem fundo escuro — apenas o spinner
   flutuante, igual ao padrão visual do ActivityGeneralForm. */
.dashboard-loading-overlay {
  position: fixed;
  top: 20vh;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  pointer-events: none;
}

.dashboard-spinner {
  width: 2.25rem;
  height: 17.25rem;
  animation: dashboard-spin 0.8s linear infinite;
  color: var(--brand);
}

@keyframes dashboard-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
